import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Coins, Loader2, TrendingUp, TrendingDown, ArrowUpRight, Landmark, Sparkle as SparkleIcon } from "lucide-react";
import { Sparkle } from "@/components/icons";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BottomNav from "@/components/BottomNav";

export default function Wallet() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [packs, setPacks] = useState([]);
  const [buying, setBuying] = useState(null);
  const [payout, setPayout] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const loadPayout = useCallback(async () => {
    try { const { data } = await api.get("/payouts/status"); setPayout(data); }
    catch { setPayout({ configured: false, connected: false }); }
  }, []);

  const load = useCallback(async () => {
    const [w, p] = await Promise.all([api.get("/coins/wallet"), api.get("/coins/packages")]);
    setWallet(w.data); setPacks(p.data);
  }, []);
  useEffect(() => { load().catch(() => {}); loadPayout(); }, [load, loadPayout]);

  // returning from Stripe Connect onboarding
  useEffect(() => {
    if (params.get("connect")) { loadPayout(); params.delete("connect"); setParams(params, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const connectBank = async () => {
    setConnecting(true);
    try { const { data } = await api.post("/payouts/connect", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(e.response?.data?.detail || "Bank payouts aren't available yet."); setConnecting(false); }
  };

  // finalize a returning coin purchase
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    api.get(`/coins/status/${sid}`).then(() => { toast.success("Coins added 🎉"); refreshUser(); load(); params.delete("session_id"); setParams(params, { replace: true }); }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const buy = async (id) => {
    setBuying(id);
    try { const { data } = await api.post("/coins/checkout", { pack_id: id, origin_url: window.location.origin }); window.location.href = data.url; }
    catch { toast.error("Couldn't start checkout"); setBuying(null); }
  };
  const withdraw = async () => {
    if (!wallet?.withdraw?.eligible) { toast.error(`You need at least ${wallet?.withdraw?.min_coins || 1000} earned coins to withdraw`); return; }
    if (payout?.configured && !payout?.payouts_enabled) { toast.error("Connect your bank account first to receive payouts"); return; }
    try {
      const { data } = await api.post("/coins/withdraw", {});
      toast.success(data.paid_out ? `Payout of $${data.net.toFixed(2)} sent to your bank 🎉` : `Withdrawal requested: $${data.net.toFixed(2)} net (after fees)`);
      refreshUser(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Withdrawal failed"); }
  };

  return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD] pb-24">
      <header className="sticky top-0 z-30 bg-ink-950/85 backdrop-blur-xl border-b border-white/10 px-4 h-14 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="wallet-back" className="text-zinc-400 hover:text-white"><ArrowLeft size={22} /></button>
        <h1 className="font-heading font-bold text-lg">Coin Wallet</h1>
      </header>

      <main className="max-w-xl mx-auto px-4 pt-5 space-y-5">
        {/* balances — Coins vs AI Credits kept clearly separate */}
        <div className="rounded-2xl border border-yellow-500/40 bg-gradient-to-br from-[#1f1808] to-ink-900 p-5" data-testid="wallet-coin-balance">
          <div className="flex items-center gap-2 text-yellow-300 text-sm"><Coins size={18} /> Coin Balance</div>
          <div className="font-heading font-black text-4xl mt-1 text-yellow-200">{wallet ? Math.round(wallet.balance) : "—"} <span className="text-lg text-yellow-400/70">coins</span></div>
          <p className="text-[11px] text-zinc-500 mt-1">Coins are for social interactions & creator earnings only — never AI generation.</p>
        </div>
        <button onClick={() => navigate("/pricing")} data-testid="wallet-credits" className="w-full rounded-2xl border border-gold/40 bg-ink-900 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gold text-sm"><Sparkle size={16} /> AI Credits (separate)</div>
          <div className="font-mono text-gold font-bold">{user?.role === "admin" ? "∞" : (user?.credits ?? 0)} →</div>
        </button>

        {/* stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-white/10 bg-ink-900 p-3 text-center"><TrendingUp size={16} className="text-green-400 mx-auto" /><div className="font-heading font-bold text-lg mt-1">{wallet ? Math.round(wallet.earned_coins) : 0}</div><div className="text-[10px] text-zinc-500">Earned</div></div>
          <div className="rounded-xl border border-white/10 bg-ink-900 p-3 text-center"><Coins size={16} className="text-yellow-400 mx-auto" /><div className="font-heading font-bold text-lg mt-1">{wallet ? Math.round(wallet.free_coins) : 0}</div><div className="text-[10px] text-zinc-500">Free / month</div></div>
          <div className="rounded-xl border border-white/10 bg-ink-900 p-3 text-center"><TrendingDown size={16} className="text-red-400 mx-auto" /><div className="font-heading font-bold text-lg mt-1">{wallet ? Math.round(wallet.spent) : 0}</div><div className="text-[10px] text-zinc-500">Spent</div></div>
        </div>
        <p className="text-[11px] text-zinc-500 -mt-2">You get {wallet?.monthly_free ?? 20} free coins each month — they don't roll over. Purchased & earned coins never expire.</p>

        {/* buy coins */}
        <div>
          <h2 className="font-heading font-bold mb-2">Buy / Recharge Coins</h2>
          <p className="text-[11px] text-zinc-500 mb-2">$1 = 70 coins.</p>
          <div className="grid grid-cols-2 gap-3">
            {packs.map((p) => (
              <button key={p.id} onClick={() => buy(p.id)} disabled={buying} data-testid={`coin-pack-${p.id}`}
                className="rounded-xl border border-white/10 bg-ink-900 p-4 text-left hover:border-yellow-500/60 transition-colors">
                <div className="flex items-center gap-1.5 text-yellow-300 font-heading font-black text-xl">{buying === p.id ? <Loader2 size={18} className="animate-spin" /> : <Coins size={18} />} {p.coins.toLocaleString()}</div>
                <div className="text-sm text-zinc-400 mt-1">${p.amount.toFixed(2)}</div>
              </button>
            ))}
          </div>
        </div>

        {/* withdraw */}
        <div className="rounded-2xl border border-white/10 bg-ink-900 p-4" data-testid="wallet-earnings">
          <div className="font-heading font-bold mb-1">Creator Earnings</div>
          <div className="text-xs text-zinc-500 mb-3">Coins are worth 1¢ each. Withdrawals: gross − Stripe fee (2.9% + $0.30) − $1 platform fee. Minimum {wallet?.withdraw?.min_coins ?? 1000} coins.</div>
          {wallet && (
            <div className="space-y-1 text-sm mb-3">
              <div className="flex justify-between"><span className="text-zinc-400">Earned ({Math.round(wallet.earned_coins)} coins)</span><span>${wallet.withdraw.gross.toFixed(2)}</span></div>
              <div className="flex justify-between text-zinc-500"><span>Stripe fee</span><span>− ${wallet.withdraw.stripe_fee.toFixed(2)}</span></div>
              <div className="flex justify-between text-zinc-500"><span>Platform fee</span><span>− ${wallet.withdraw.platform_fee.toFixed(2)}</span></div>
              <div className="flex justify-between font-heading font-bold border-t border-white/10 pt-1 mt-1"><span>You receive</span><span className={wallet.withdraw.net > 0 ? "text-green-400" : "text-zinc-500"}>${Math.max(0, wallet.withdraw.net).toFixed(2)}</span></div>
            </div>
          )}
          {/* Bank connection (Stripe Connect) */}
          {payout && (
            payout.payouts_enabled ? (
              <div className="flex items-center gap-2 text-xs text-green-400 mb-3" data-testid="bank-connected"><Landmark size={14} /> Bank account connected — withdrawals pay out to your bank.</div>
            ) : payout.configured ? (
              <button onClick={connectBank} disabled={connecting} data-testid="connect-bank" className="w-full flex items-center justify-center gap-2 border border-gold/50 text-gold rounded-full px-4 py-2.5 text-sm font-heading font-bold mb-3 disabled:opacity-50">
                {connecting ? <Loader2 size={15} className="animate-spin" /> : <Landmark size={15} />} {payout.connected ? "Finish bank setup" : "Connect bank account"}
              </button>
            ) : (
              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3" data-testid="bank-soon"><Landmark size={14} /> Bank payouts activate soon. Withdrawals are logged and paid once payouts go live.</div>
            )
          )}
          <button onClick={withdraw} disabled={!wallet?.withdraw?.eligible} data-testid="wallet-withdraw" className="w-full flex items-center justify-center gap-1.5 bg-gold text-ink-950 font-heading font-bold text-sm rounded-full px-4 py-2.5 disabled:opacity-50"><ArrowUpRight size={15} /> Withdraw earnings</button>
          {wallet && !wallet.withdraw?.eligible && <p className="text-[11px] text-zinc-500 text-center mt-2">Requires {wallet?.withdraw?.min_coins ?? 1000} earned coins (${((wallet?.withdraw?.min_coins ?? 1000) * (wallet?.withdraw?.coin_usd ?? 0.01)).toFixed(0)}) to withdraw.</p>}
        </div>

        {/* transactions */}
        <div>
          <h2 className="font-heading font-bold mb-2">Transaction History</h2>
          <div className="space-y-2" data-testid="wallet-transactions">
            {!wallet ? <Loader2 className="animate-spin text-gold mx-auto" size={22} />
              : wallet.transactions.length === 0 ? <p className="text-sm text-zinc-600 text-center py-6">No coin activity yet.</p>
              : wallet.transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between border border-white/5 rounded-lg px-3 py-2.5">
                  <div className="text-sm capitalize">{t.reason?.replace(/_/g, " ") || t.type}<div className="text-[10px] text-zinc-600">{new Date(t.created_at).toLocaleString()}</div></div>
                  <span className={`font-mono font-bold text-sm ${t.amount >= 0 ? "text-green-400" : "text-red-400"}`}>{t.amount >= 0 ? "+" : ""}{Math.round(t.amount * 100) / 100}</span>
                </div>
              ))}
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
